import { extractOtpFromText } from '../util/otpUtils';

/**
 * Provides the props needed to enable native OTP autofill (iOS/Android) and
 * smart paste on a single OTP input field, keeping every OTP form consistent.
 *
 * Spread the returned object on the OTP <Form.Control /> / <input />. It sets:
 *  - autoComplete="one-time-code", inputMode="numeric", pattern="[0-9]*" so the
 *    OS suggests the SMS code and the numeric keyboard is shown on mobile.
 *  - an onPaste handler that extracts the code from the pasted content (e.g. a
 *    full SMS body) and fills the field, ignoring the surrounding text.
 *
 * @param {(code: string) => void} setOtpCode - Setter for the OTP value.
 * @param {number} [expectedLength] - Exact OTP length, when known by the caller.
 * @returns {object} Props to spread on the OTP input element.
 */
export const useOtpInput = (setOtpCode, expectedLength) => {
  const handlePaste = (event) => {
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const code = extractOtpFromText(pastedText, expectedLength);

    // When a valid code is found, replace the field content with just the code.
    // Otherwise let the browser paste normally so manual entry keeps working.
    if (code) {
      event.preventDefault();
      setOtpCode(code);
    }
  };

  return {
    type: 'text',
    autoComplete: 'one-time-code',
    inputMode: 'numeric',
    pattern: '[0-9]*',
    onPaste: handlePaste,
  };
};
